import { Fragment } from "react";
import { create } from "zustand";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  showDialog: (options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  hideDialog: () => void;
}

export const useConfirmDialog = create<ConfirmDialogState>((set) => ({
  isOpen: false,
  title: "",
  message: "",
  confirmText: "Confirm",
  cancelText: "Cancel",
  onConfirm: () => { },
  onCancel: () => { },
  showDialog: (options) => {
    set({
      isOpen: true,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText || "Confirm",
      cancelText: options.cancelText || "Cancel",
      onConfirm: options.onConfirm,
      onCancel: options.onCancel || (() => { }),
    });
  },
  hideDialog: () => {
    set({
      isOpen: false,
    });
  },
}));

export function ConfirmDialog() {
  const { t } = useTranslation("common");
  const {
    isOpen,
    title,
    message,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    hideDialog,
  } = useConfirmDialog();

  const handleConfirm = () => {
    onConfirm();
    hideDialog();
  };

  const handleCancel = () => {
    onCancel();
    hideDialog();
  };

  // Use translations for default button texts
  const actualConfirmText =
    confirmText === "Confirm" ? t("actions.confirm") : confirmText;
  const actualCancelText =
    cancelText === "Cancel" ? t("actions.cancel") : cancelText;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-neutral-900 p-6 shadow-xl transition-all border border-transparent dark:border-neutral-800">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-semibold text-text-primary mb-2"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">{message}</p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-text-primary border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    {actualCancelText}
                  </button>
                  <button
                    onClick={handleConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-error-500 rounded-lg hover:bg-error-600 transition-colors"
                  >
                    {actualConfirmText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
